// Central per-user screen-access hook. Reads the logged-in user's screen_access
// map (surfaced on adminProfile from /users/customers/{mobile}) and exposes the
// checks used by SideNavbar, RequireAuth, and per-screen write gating.
//
// Rules:
//  - Admin & SuperAdmin always have full write to every screen (never gated).
//  - Employee / Supervisor / Cashier are gated by their screen_access map.
//  - Default screens (calculators, referral, privacy, support) are never gated
//    here — they are always accessible and are not part of the catalog.

import { useMemo } from 'react';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store';
import type { AccessLevel, ScreenKey } from '../config/screens';

export interface Permissions {
  isPrivileged: boolean; // Admin or SuperAdmin -> full access
  isSuperAdmin: boolean;
  // True only when the backend has returned a NON-EMPTY screen_access map for
  // this user. Enforcement (nav hiding, route guards, write gating) activates
  // only when this is true — so users on a backend that predates the feature,
  // or staff the SuperAdmin hasn't configured yet, keep their legacy access
  // instead of being locked out.
  hasScreenConfig: boolean;
  screenAccess: Record<string, AccessLevel>;
  levelFor: (key: ScreenKey) => AccessLevel;
  canView: (key: ScreenKey) => boolean;
  canWrite: (key: ScreenKey) => boolean;
  // True ONLY for a configured staff member whose access to this screen is
  // read-only. Unconfigured users and privileged roles are never read-only, so
  // gating with `!isReadOnly(key)` never removes an existing capability — it only
  // disables write actions for staff explicitly granted read access.
  isReadOnly: (key: ScreenKey) => boolean;
  // Granular write sub-action check. True when the user may perform `action`
  // on `screen`. Privileged (Admin/SuperAdmin) always true. Otherwise the user
  // must have WRITE on the screen; if they have a screen_actions list for that
  // screen, the action must be in it — if they have write but NO action list,
  // all actions are allowed (full write, backward-compatible).
  can: (screen: ScreenKey, action: string) => boolean;
  // Graceful sub-action gate for use in components. `legacy` is the button's
  // original visibility condition (default true). Unconfigured users fall back
  // to legacy; configured users are governed authoritatively by `can()`.
  canAction: (screen: ScreenKey, action: string, legacy?: boolean) => boolean;
  screenActions: Record<string, string[]>;
}

export function usePermissions(): Permissions {
  const adminProfile = useAppSelector((s: RootState) => (s.users as any).adminProfile);
  const adminRole = useAppSelector((s: RootState) => (s.auth as any).adminRole);

  return useMemo(() => {
    const role = String(adminProfile?.role || adminRole || '').toLowerCase();
    const isSuperAdmin = role.includes('superadmin');
    // Only SuperAdmin fully bypasses screen-access. Admins keep their existing
    // full access via graceful degradation: they are NOT configurable by anyone
    // (see StaffAccessTab / backend STAFF_MANAGE_MATRIX), so their screen_access
    // stays empty → hasScreenConfig=false → every gate falls back to the original
    // role-based behavior. This preserves legacy Admin capabilities exactly
    // (e.g. product write stays SuperAdmin-only) without granting anything new.
    const isPrivileged = isSuperAdmin;
    const screenAccess: Record<string, AccessLevel> = (adminProfile?.screen_access || {}) as Record<string, AccessLevel>;
    const screenActions: Record<string, string[]> = (adminProfile?.screen_actions || {}) as Record<string, string[]>;
    const hasScreenConfig = !!screenAccess && Object.keys(screenAccess).length > 0;

    const levelFor = (key: ScreenKey): AccessLevel => {
      if (isPrivileged) return 'write';
      const v = screenAccess[key];
      return v === 'write' ? 'write' : v === 'read' ? 'read' : 'none';
    };

    return {
      isPrivileged,
      isSuperAdmin,
      hasScreenConfig,
      screenAccess,
      levelFor,
      canView: (key: ScreenKey) => levelFor(key) !== 'none',
      canWrite: (key: ScreenKey) => levelFor(key) === 'write',
      isReadOnly: (key: ScreenKey) =>
        !isPrivileged && hasScreenConfig && screenAccess[key] === 'read',
      screenActions,
      can: (key: ScreenKey, action: string) => {
        if (isPrivileged) return true;
        if (levelFor(key) !== 'write') return false;
        const list = screenActions[key];
        // Key absent => no granular config for this screen => full write (all
        // actions) — backward-compatible with grants made before sub-actions.
        // A present list (even empty) is authoritative: only its actions allowed.
        if (list === undefined) return true;
        return list.includes(action);
      },
      canAction: (key: ScreenKey, action: string, legacy: boolean = true) => {
        if (isPrivileged) return true;
        if (!hasScreenConfig) return legacy; // unconfigured => legacy role behavior
        if (levelFor(key) !== 'write') return false; // read/none => no operations
        const list = screenActions[key];
        if (list === undefined) return true; // write, no granular list => all actions
        return list.includes(action);
      },
    };
  }, [adminProfile, adminRole]);
}
